import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
    UserPlus, 
    Gift, 
    ArrowRight, 
    ArrowLeft,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubmitReferralPage = () => {
    const { orgCode } = useParams();
    const { token } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    
    const [step, setStep] = useState(1);
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        lead_name: '',
        lead_email: '',
        lead_phone: '',
        lead_company: '',
        lead_street: '',
        lead_zip: '',
        lead_city: '',
        lead_notes: '',
        consent_confirmed: false
    });

    useEffect(() => {
        fetchRewards();
    }, []);

    const fetchRewards = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/rewards`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRewards(response.data);
        } catch (error) {
            console.error('Failed to fetch rewards:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (!formData.consent_confirmed) {
            toast.error('Bitte bestätigen Sie, dass die Empfehlung kontaktiert werden darf');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/referrals`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Empfehlung erfolgreich eingereicht!');
            setStep(3); // Success step
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Empfehlung konnte nicht eingereicht werden');
        } finally {
            setSubmitting(false);
        }
    };

    const isStep1Valid = formData.lead_name && formData.lead_email && formData.lead_phone && formData.consent_confirmed;

    return (
        <DashboardLayout>
            <div className="max-w-2xl mx-auto animate-fade-in">
                {/* Progress Steps */}
                <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center gap-4">
                        <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                                step >= 1 ? 'text-white' : 'bg-slate-200 text-slate-600'
                            }`}
                            style={{ backgroundColor: step >= 1 ? tenant?.primary_color : undefined }}
                        >
                            {step > 1 ? <CheckCircle2 size={20} /> : '1'}
                        </div>
                        <div className={`w-20 h-1 ${step > 1 ? '' : 'bg-slate-200'}`} style={{ backgroundColor: step > 1 ? tenant?.primary_color : undefined }} />
                        <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                                step >= 2 ? 'text-white' : 'bg-slate-200 text-slate-600'
                            }`}
                            style={{ backgroundColor: step >= 2 ? tenant?.primary_color : undefined }}
                        >
                            {step > 2 ? <CheckCircle2 size={20} /> : '2'}
                        </div>
                        <div className={`w-20 h-1 ${step > 2 ? '' : 'bg-slate-200'}`} style={{ backgroundColor: step > 2 ? tenant?.primary_color : undefined }} />
                        <div 
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                                step >= 3 ? 'text-white' : 'bg-slate-200 text-slate-600'
                            }`}
                            style={{ backgroundColor: step >= 3 ? tenant?.primary_color : undefined }}
                        >
                            3
                        </div>
                    </div>
                </div>

                {/* Step 1: Referral Entry Form */}
                {step === 1 && (
                    <Card data-testid="referral-form-step1">
                        <CardHeader>
                            <div className="flex items-center gap-3 mb-2">
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                >
                                    <UserPlus size={24} style={{ color: tenant?.primary_color }} />
                                </div>
                            </div>
                            <CardTitle className="text-2xl">Empfehlung eintragen</CardTitle>
                            <CardDescription>
                                Geben Sie die Daten der Person ein, die Sie empfehlen möchten
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                {/* Required fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="lead_name">Name der Empfehlung *</Label>
                                        <Input
                                            id="lead_name"
                                            name="lead_name"
                                            placeholder="Vollständiger Name"
                                            value={formData.lead_name}
                                            onChange={handleInputChange}
                                            data-testid="lead-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lead_company">Firma</Label>
                                        <Input
                                            id="lead_company"
                                            name="lead_company"
                                            placeholder="Firmenname (optional)"
                                            value={formData.lead_company}
                                            onChange={handleInputChange}
                                            data-testid="lead-company-input"
                                        />
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="lead_email">E-Mail *</Label>
                                        <Input
                                            id="lead_email"
                                            name="lead_email"
                                            type="email"
                                            placeholder="email@beispiel.de"
                                            value={formData.lead_email}
                                            onChange={handleInputChange}
                                            data-testid="lead-email-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lead_phone">Telefon *</Label>
                                        <Input
                                            id="lead_phone"
                                            name="lead_phone"
                                            type="tel"
                                            placeholder="+49 (0) 123 456789"
                                            value={formData.lead_phone}
                                            onChange={handleInputChange}
                                            data-testid="lead-phone-input"
                                        />
                                    </div>
                                </div>
                                
                                {/* Address fields */}
                                <div className="pt-2 border-t border-slate-100">
                                    <p className="text-sm text-slate-500 mb-3">Adresse (optional)</p>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="lead_street">Straße und Hausnummer</Label>
                                            <Input
                                                id="lead_street"
                                                name="lead_street"
                                                placeholder="Musterstraße 123"
                                                value={formData.lead_street}
                                                onChange={handleInputChange}
                                                data-testid="lead-street-input"
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="lead_zip">PLZ</Label>
                                                <Input
                                                    id="lead_zip"
                                                    name="lead_zip"
                                                    placeholder="12345"
                                                    value={formData.lead_zip}
                                                    onChange={handleInputChange}
                                                    data-testid="lead-zip-input"
                                                />
                                            </div>
                                            <div className="space-y-2 col-span-2">
                                                <Label htmlFor="lead_city">Ort</Label>
                                                <Input
                                                    id="lead_city"
                                                    name="lead_city"
                                                    placeholder="Musterstadt"
                                                    value={formData.lead_city}
                                                    onChange={handleInputChange}
                                                    data-testid="lead-city-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Notes */}
                                <div className="space-y-2">
                                    <Label htmlFor="lead_notes">Zusätzliche Informationen</Label>
                                    <textarea
                                        id="lead_notes"
                                        name="lead_notes"
                                        placeholder="Weitere relevante Informationen zur Empfehlung..."
                                        value={formData.lead_notes}
                                        onChange={handleInputChange}
                                        className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        data-testid="lead-notes-input"
                                    />
                                </div>
                            </div>

                            <div className="bg-slate-50 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="consent"
                                        checked={formData.consent_confirmed}
                                        onCheckedChange={(checked) => 
                                            setFormData({ ...formData, consent_confirmed: checked })
                                        }
                                        data-testid="consent-checkbox"
                                    />
                                    <label 
                                        htmlFor="consent" 
                                        className="text-sm text-slate-600 cursor-pointer leading-relaxed"
                                    >
                                        Ich bestätige, dass die empfohlene Person der Kontaktaufnahme im Rahmen dieser Empfehlung zugestimmt hat.
                                    </label>
                                </div>
                            </div>

                            <Button
                                className="w-full"
                                style={{ backgroundColor: tenant?.primary_color }}
                                disabled={!isStep1Valid}
                                onClick={() => setStep(2)}
                                data-testid="continue-to-rewards-btn"
                            >
                                Weiter zu den Prämien
                                <ArrowRight size={18} className="ml-2" />
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Step 2: Rewards Overview */}
                {step === 2 && (
                    <Card data-testid="referral-form-step2">
                        <CardHeader>
                            <div className="flex items-center gap-3 mb-2">
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                >
                                    <Gift size={24} style={{ color: tenant?.primary_color }} />
                                </div>
                            </div>
                            <CardTitle className="text-2xl">Verfügbare Prämien</CardTitle>
                            <CardDescription>
                                Diese Prämien können Sie mit Ihren Punkten einlösen
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {loading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />
                                    ))}
                                </div>
                            ) : rewards.length === 0 ? (
                                <div className="text-center py-8">
                                    <Gift size={48} className="mx-auto text-slate-300 mb-3" />
                                    <p className="text-slate-500">Derzeit keine Prämien verfügbar</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {rewards.map((reward) => (
                                        <div 
                                            key={reward.id}
                                            className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                                        >
                                            <div 
                                                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                            >
                                                <Gift size={22} style={{ color: tenant?.primary_color }} />
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-slate-900">{reward.title}</p>
                                                {reward.description && (
                                                    <p className="text-sm text-slate-500">{reward.description}</p>
                                                )}
                                            </div>
                                            <div 
                                                className="text-lg font-bold"
                                                style={{ color: tenant?.primary_color }}
                                            >
                                                {reward.points_required} Pkt.
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setStep(1)}
                                    data-testid="back-to-form-btn"
                                >
                                    <ArrowLeft size={18} className="mr-2" />
                                    Zurück
                                </Button>
                                <Button
                                    className="flex-1"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    data-testid="submit-referral-btn"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Wird eingereicht...
                                        </>
                                    ) : (
                                        <>
                                            Empfehlung einreichen
                                            <ArrowRight size={18} className="ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Step 3: Success */}
                {step === 3 && (
                    <Card className="text-center" data-testid="referral-success">
                        <CardContent className="pt-12 pb-8">
                            <div 
                                className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                            >
                                <CheckCircle2 size={40} style={{ color: tenant?.primary_color }} />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                Empfehlung eingereicht!
                            </h2>
                            <p className="text-slate-600 mb-8 max-w-md mx-auto">
                                Vielen Dank für Ihre Empfehlung. Sie erhalten Punkte, sobald die Empfehlung konvertiert wird.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setStep(1);
                                        setFormData({
                                            lead_name: '',
                                            lead_email: '',
                                            lead_phone: '',
                                            lead_company: '',
                                            lead_street: '',
                                            lead_zip: '',
                                            lead_city: '',
                                            lead_notes: '',
                                            consent_confirmed: false
                                        });
                                    }}
                                    data-testid="submit-another-btn"
                                >
                                    <UserPlus size={18} className="mr-2" />
                                    Weitere Empfehlung eintragen
                                </Button>
                                <Button
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    onClick={() => navigate(`/o/${orgCode}/dashboard`)}
                                    data-testid="go-to-dashboard-btn"
                                >
                                    Zur Übersicht
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DashboardLayout>
    );
};
